import { type FC } from "react";
import styled from "styled-components";
import { Typography, TypographyVariant } from "../../../../components/ui-kit";
import { CompanySubscriptionHistoryEntry } from "../../../../util/types";
import {
  HistoryStatusTone,
  historyDiscountLabel,
  historyPlanLabel,
  historyPricingLabel,
  historyStatus,
  sortHistoryEntries,
} from "./subscription-history.helpers";

/**
 * Chronology of every subscription the company ever had, newest first —
 * including cancelled ones, which the tabs above deliberately hide. Read
 * only: the terms of a cancelled Payments subscription live on in the
 * `Preconfigured` draft that cancellation leaves behind, so edits happen
 * there.
 */

const Wrap = styled.div`
  margin-top: 28px;
`;

const Caption = styled.div`
  font-size: 12px;
  color: #888;
  margin: 4px 0 12px;
`;

const Scroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const Th = styled.th`
  text-align: left;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  font-weight: 700;
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid #f3f3f3;
  vertical-align: top;
  color: #333;
`;

const Muted = styled.span`
  color: #aaa;
`;

const Sub = styled.div`
  font-size: 11px;
  color: #888;
  margin-top: 2px;
`;

const Pre = styled.div`
  white-space: pre-line;
  max-width: 320px;
`;

const TONE_STYLE: Record<HistoryStatusTone, { bg: string; fg: string }> = {
  live: { bg: "#dcfce7", fg: "#166534" },
  warn: { bg: "#fef3c7", fg: "#92400e" },
  draft: { bg: "#ede9fe", fg: "#5b21b6" },
  ended: { bg: "#fee2e2", fg: "#991b1b" },
  muted: { bg: "#f3f4f6", fg: "#6b7280" },
};

const Pill = styled.span<{ $tone: HistoryStatusTone }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $tone }) => TONE_STYLE[$tone].bg};
  color: ${({ $tone }) => TONE_STYLE[$tone].fg};
`;

const Empty = styled.div`
  padding: 14px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  color: #888;
`;

const fmtDate = (iso?: string | null) =>
  !iso
    ? "—"
    : new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

export const SubscriptionHistory: FC<{
  entries: CompanySubscriptionHistoryEntry[];
  loading?: boolean;
}> = ({ entries, loading = false }) => {
  const rows = sortHistoryEntries(entries);

  return (
    <Wrap>
      <Typography variant={TypographyVariant.HEADER3}>
        Subscription history
      </Typography>
      <Caption>
        Every subscription this company has had, newest first, including
        cancelled ones. A cancelled Payments subscription leaves its terms in a
        Preconfigured draft on the product tab above.
      </Caption>

      {loading && !rows.length ? (
        <Empty>Loading…</Empty>
      ) : !rows.length ? (
        <Empty>No subscriptions yet.</Empty>
      ) : (
        <Scroll>
          <Table>
            <thead>
              <tr>
                <Th>Product</Th>
                <Th>Status</Th>
                <Th>Currency</Th>
                <Th>Pricing</Th>
                <Th>Discount / trial</Th>
                <Th>Created</Th>
                <Th>Period</Th>
                <Th>Ended</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => {
                const status = historyStatus(entry);
                const discount = historyDiscountLabel(entry);
                const trial =
                  entry.trialPeriodMode === "none"
                    ? "no trial"
                    : entry.trialPeriodMode === "custom" &&
                      entry.trialPeriodDays
                    ? `${entry.trialPeriodDays}-day trial`
                    : entry.trialPeriodMode === "default"
                    ? "default trial"
                    : null;
                const euCurrencies = entry.enabledPaymentCurrencies?.length
                  ? entry.enabledPaymentCurrencies
                      .map((c) => c.toUpperCase())
                      .join(", ")
                  : null;
                return (
                  <tr key={entry.subscriptionId}>
                    <Td>
                      {historyPlanLabel(entry)}
                      {euCurrencies && <Sub>Payments in {euCurrencies}</Sub>}
                    </Td>
                    <Td>
                      <Pill $tone={status.tone}>{status.label}</Pill>
                    </Td>
                    <Td>
                      {entry.currency ? (
                        entry.currency.toUpperCase()
                      ) : (
                        <Muted>customer decides</Muted>
                      )}
                    </Td>
                    <Td>
                      <Pre>{historyPricingLabel(entry)}</Pre>
                      {entry.interimBillingInterval && (
                        <Sub>
                          Interim billing: {entry.interimBillingInterval}
                        </Sub>
                      )}
                    </Td>
                    <Td>
                      {discount ?? <Muted>—</Muted>}
                      {trial && <Sub>{trial}</Sub>}
                    </Td>
                    <Td>{fmtDate(entry.createdAt)}</Td>
                    <Td>
                      {entry.currentPeriodStart || entry.currentPeriodEnd ? (
                        <>
                          {fmtDate(entry.currentPeriodStart)} →{" "}
                          {fmtDate(entry.currentPeriodEnd)}
                        </>
                      ) : (
                        <Muted>—</Muted>
                      )}
                    </Td>
                    <Td>
                      {entry.canceledAt ? (
                        fmtDate(entry.canceledAt)
                      ) : entry.cancelAtPeriodEnd ? (
                        <>
                          at period end
                          {entry.cancellationRequestedAt && (
                            <Sub>
                              requested {fmtDate(entry.cancellationRequestedAt)}
                            </Sub>
                          )}
                        </>
                      ) : (
                        <Muted>—</Muted>
                      )}
                      {entry.cancellationReason && (
                        <Sub>Reason: {entry.cancellationReason}</Sub>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Scroll>
      )}
    </Wrap>
  );
};

export default SubscriptionHistory;
